import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import eventBus from "../utils/eventBus";
import { getLoginCredentials } from "../services/apiServices";
import { useQueryString } from "./useQueryString";
import { BW_PROMO_DEGRADED_CONFIG_DEFAULT } from "../assets/const/portal.const";
import { AUTH_EVENTS, USER_EVENTS } from "../utils/eventConstants";
import { getVenueDetailsFromUrl } from "../utils/utils";

interface CustomerLoginProps {
  username: string;
  password: string;
  isWLC?: boolean;
  isSmartzoneLogin?: boolean;
  [key: string]: any;
}

interface DeadmanLoginProps {
  [key: string]: any;
  username: string;
  password: string;
  degraded: boolean;
}

// Define response interface for deadmanLogin
interface DeadmanLoginResponse {
  status?: number;
  username: string;
  password: string;
  loginConfig?: LoginTypeConfig;
  statusText?: string;
  [key: string]: any;
}

interface LoginTypeConfig {
  clientMac: string;
  apMac: string;
  loginType: "via" | "smartzone" | "wlc";
}

interface LoginCredentials {
  username: string;
  password: string;
  isWLC: boolean;
  isSmartzoneLogin: boolean;
  link_login_only?: string;
  mac?: string;
  ip?: string;
  user?: string;
  cmd?: string;
  buttonClicked?: string;
}

export const useAuthentication = () => {
  const { t } = useTranslation();
  const [isCustomerCreated, setIsCustomerCreated] = useState(false);
  const [customerLoginData, setCustomerLoginData] =
    useState<CustomerLoginProps | null>(null);
  const [dialogMessage, setDialogMessage] = useState("");
  const navigate = useNavigate();

  const { getQueryParams } = useQueryString();
  const queryParams = getQueryParams({ urlDecode: true });

  const handleAuthSuccess = useCallback(
    (data: any) => {
      console.log("Authentication successful", data);
      setDialogMessage(
        t(
          "signup_page.auth_message.auth_success",
          "Authentication successful. Redirecting..."
        )
      );

      const { venueId, sublocation, productType } =
        getVenueDetailsFromUrl() || {};
      // Construct full venue identifier with sublocation if it exists
      const fullVenueId = sublocation ? `${venueId}.${sublocation}` : venueId;

      const isLocalhost =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";
      eventBus.dispatch(USER_EVENTS.USER_LOGIN, { ...data, venue_id: venueId });

      //for media redirection, if accept button is click
      if (queryParams?.redirect_url) {
        window.location.href = queryParams.redirect_url;
      }
      else if (productType && venueId) {
        const newPath = isLocalhost
          ? `/${productType}/${fullVenueId}/welcome`
          : `/${fullVenueId}/welcome`;

        // Append query string if it exists
        const queryString = window.location.search;
        const finalPath = queryString ? `${newPath}${queryString}` : newPath;

        window.location.href = isLocalhost
          ? `${window.location.origin}${finalPath}`
          : finalPath;
      }
    },
    [navigate, t]
  );

  const handleAuthError = useCallback(
    (error: any) => {
      console.error("Authentication failed", error);
      let errorMessage;

      if (error.includes("Missing required fields:")) {
        errorMessage = t(
          "signup_page.auth_message.missing_fields_error",
          "{{error}}. User was created, please contact support.",
          {
            error: error,
          }
        );
      } else {
        errorMessage = t(
          "signup_page.auth_message.general_auth_error",
          "{{error}}. Please try again.",
          {
            error: error,
          }
        );
      }
      const { venueId } = getVenueDetailsFromUrl() || {};
      eventBus.dispatch(USER_EVENTS.USER_LOGIN_ERROR, {
        error: errorMessage,
        venue_id: venueId,
      });
      setDialogMessage(errorMessage);
    },
    [t]
  );

  const initiateAuthentication = useCallback(
    (loginData: CustomerLoginProps) => {
      setCustomerLoginData(loginData);
      setIsCustomerCreated(true);
    },
    []
  );

  const determineLoginType = (): LoginTypeConfig => {
    const defaultMac = "00:00:00:00:00:00";
    const isSmartzoneLogin = Boolean(queryParams.nbiIP && queryParams.uip);
    const isWLCLogin = Boolean(queryParams.switch_url);

    if (isSmartzoneLogin) {
      return {
        clientMac: queryParams.client_mac || defaultMac,
        apMac: queryParams.mac || defaultMac,
        loginType: "smartzone",
      };
    }

    if (isWLCLogin) {
      return {
        clientMac: queryParams.client_mac || defaultMac,
        apMac: queryParams.ap_mac || defaultMac,
        loginType: "wlc",
      };
    }

    // Default VIA login
    return {
      clientMac: queryParams.mac || defaultMac,
      apMac: queryParams.ap_mac || defaultMac,
      loginType: "via",
    };
  };

  const createLoginCredentials = (
    response: any,
    venue_configs: DeadmanLoginProps,
    loginConfig: LoginTypeConfig
  ): LoginCredentials => {
    const baseCredentials = {
      ...response.data,
      isSmartzoneLogin: venue_configs.isSmartzoneLogin,
      isWLC: venue_configs.isWLC,
    };

    switch (loginConfig.loginType) {
      case "smartzone":
        return {
          ...baseCredentials,
          isSmartzoneLogin: true,
          link_login_only: queryParams.nbiIP,
          mac: loginConfig.clientMac,
          ip: queryParams.uip,
        };

      case "wlc":
        return {
          ...baseCredentials,
          isWLC: true,
          user: response.data.username,
          cmd: "authenticate",
          buttonClicked: "4",
          mac: loginConfig.clientMac,
          link_login_only: queryParams.switch_url,
        };

      default: // 'via'
        return {
          ...baseCredentials,
          link_login_only: queryParams.link_login_only,
        };
    }
  };

  // Add deadmanLogin function for testing when API is down
  const deadmanLogin = useCallback(
    async (venue_configs: DeadmanLoginProps): Promise<DeadmanLoginResponse> => {
      try {
        console.log(
          "Deadman login initiated with venue_configs:",
          venue_configs
        );

        let bwPromoLoginCredentials = {
          mac: queryParams.mac,
          venue_id: venue_configs.venue_id,
          bwpromo_config: {
            ...BW_PROMO_DEGRADED_CONFIG_DEFAULT,
          },
        };

        if (!bwPromoLoginCredentials.mac) {
          throw new Error(
            t(
              "signup_page.auth_message.missing_mac",
              "Missing required MAC address for deadman login"
            )
          );
        }

        const response = await getLoginCredentials(
          bwPromoLoginCredentials,
          "bwpromo"
        );

        if (response.status === 200) {
          const loginConfig = determineLoginType();
          const initialLogin = createLoginCredentials(
            response,
            venue_configs,
            loginConfig
          );

          eventBus.dispatch(AUTH_EVENTS.DEADMAN_LOGIN, venue_configs);

          console.log("Initial login credentials with bwpromo:", initialLogin);
          //initiateAuthentication(initialLogin);

          return {
            status: response.status,
            ...initialLogin,
          };
        } else {
          console.error("Error getting login bwpromo credentials", response);
          setDialogMessage(
            t(
              "signup_page.auth_message.bwpromo_error",
              "Failed to get bwpromo login credentials: {{error}}",
              {
                error: response.statusText || "Unknown error",
              }
            )
          );

          return {
            status: response.status || 400,
            statusText: response.statusText || "Unknown error",
            username: venue_configs.username,
            password: venue_configs.password,
          };
        }
      } catch (error) {
        console.error("Exception in deadman login:", error);
        setDialogMessage(
          t(
            "signup_page.auth_message.login_process_error",
            "Login process failed: {{error}}",
            {
              error: error instanceof Error ? error.message : "Unknown error",
            }
          )
        );

        eventBus.dispatch(AUTH_EVENTS.DEADMAN_LOGIN_ERROR, {
          configsCredentials: venue_configs,
          error: error instanceof Error ? error.message : "Unknown error",
        });

        return {
          status: 500,
          statusText: error instanceof Error ? error.message : "Unknown error",
          username: venue_configs.username,
          password: venue_configs.password,
        };
      }
    },
    [initiateAuthentication, setDialogMessage, t, queryParams.mac]
  );

  return {
    isCustomerCreated,
    customerLoginData,
    dialogMessage,
    handleAuthSuccess,
    handleAuthError,
    initiateAuthentication,
    setDialogMessage,
    deadmanLogin,
  };
};
